/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Eye, Download, FileSpreadsheet, FileText, ChevronLeft, ChevronRight } from "lucide-react";
import apiService from "@/services/apiService";
import { DocumentViewPopup } from "@/components/document-view-popup";
import { getUserInfo } from "@/services/userService";

export type TypeKey = "107" | "111" | "117" | "116_3";

interface TypeConfig {
  label: string;
  psColumn: TypeKey;
  // Existence flags forwarded to GET /documents to scope the detail list.
  docFilter: Record<string, string>;
  // Key on the ps-wise-report station object holding this type's bond sum.
  bondKey: "bondAmount111" | "bondAmount117" | null;
  bondLabel: string | null;
}

const DOC_TYPE_CONFIG: Record<TypeKey, TypeConfig> = {
  "107": { label: "CrPC 107 / BNSS 126", psColumn: "107", docFilter: {}, bondKey: null, bondLabel: null },
  "111": { label: "CrPC 111 / BNSS 130", psColumn: "111", docFilter: { hasCrpc129Notice: "true" }, bondKey: "bondAmount111", bondLabel: "Bond Amt (111)" },
  "117": { label: "CrPC 117 / BNSS 136", psColumn: "117", docFilter: { hasCrpcBondOrder: "true" }, bondKey: "bondAmount117", bondLabel: "Bond Amt (117)" },
  "116_3": { label: "CrPC 116(3) / BNSS 135(3)", psColumn: "116_3", docFilter: { hasBNSL129Notice: "true" }, bondKey: null, bondLabel: null },
};

const RISK_OPTIONS = [
  { value: "all", label: "All Risk" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

interface SummaryRow {
  id: string;
  circle: string;
  station: string;
  count: number;
  bond: number;
}

const ITEMS_PER_PAGE = 10;

const getBondAmount = (doc: any) => {
  if (doc.crpc129Notice?.boundDownOrder?.bondAmount) return doc.crpc129Notice.boundDownOrder.bondAmount;
  if (doc.bNSL129Notice?.boundDownOrder?.bondAmount) return doc.bNSL129Notice.boundDownOrder.bondAmount;
  if (doc.crpcBondOrder?.boundDownOrder?.bondAmount) return doc.crpcBondOrder.boundDownOrder.bondAmount;
  return undefined;
};

const formatCapitalize = (str: string) => {
  if (!str) return str;
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

const formatDate = (value?: string) => (value ? new Date(value).toLocaleDateString("en-IN") : "-");

const toDetailRow = (doc: any) => ({
  id: doc.id,
  gdNo: doc.generalDiary?.gdNo || "-",
  caseType: doc.generalDiary?.caseType,
  officer: doc.generalDiary?.entryForOfficer,
  station: doc.generalDiary?.policeStation?.name || doc.generalDiary?.policeStation?.nameEn || "-",
  gdDate: formatDate(doc.generalDiary?.gdDateTime),
  uploadDate: formatDate(doc.createdAt),
  bondAmount: getBondAmount(doc),
  riskLevel: formatCapitalize(doc.generalDiary?.riskLevel),
  data: doc,
});

export default function DocumentTypeReport({ typeKey }: { typeKey: TypeKey }) {
  const config = DOC_TYPE_CONFIG[typeKey];

  // Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const dateBasis = "updated";
  const [policeStation, setPoliceStation] = useState("all");
  const [riskLevel, setRiskLevel] = useState("all");

  // Summary
  const [summaryRows, setSummaryRows] = useState<SummaryRow[]>([]);
  const [stationOptions, setStationOptions] = useState<{ name: string; circle: string }[]>([]);

  // Detail
  const [documents, setDocuments] = useState<any[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);

  // Document detail popup
  const [selectedDocument, setSelectedDocument] = useState<any>(null);
  const [showDocumentDetail, setShowDocumentDetail] = useState(false);

  const userId = useMemo(() => {
    const userInfo: any = getUserInfo();
    return userInfo?.id ?? null;
  }, []);

  // Build the query params shared by the summary and detail requests.
  const buildDocParams = useCallback(
    (pageNum: number, exportAll = false) => {
      const params: any = { ...config.docFilter, page: pageNum, limit: exportAll ? 100000 : ITEMS_PER_PAGE };
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (startDate || endDate) params.dateBasis = dateBasis;
      if (policeStation !== "all") params.policeStation = policeStation;
      if (riskLevel !== "all") params.riskLevel = riskLevel;
      return params;
    },
    [config.docFilter, startDate, endDate, dateBasis, policeStation, riskLevel]
  );

  const loadSummary = useCallback(async () => {
    const params: any = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (startDate || endDate) params.dateBasis = dateBasis;
    if (userId) params.user_id = userId;

    const report: any = await apiService.get(`reports/get-ps-wise-report`, params, { noCache: true });

    const rows: SummaryRow[] = [];
    (report || []).forEach((circle: any) => {
      (circle.stations || []).forEach((station: any) => {
        rows.push({
          id: station.id,
          circle: circle.circle,
          station: station.name,
          count: station.documents?.[config.psColumn] ?? 0,
          bond: config.bondKey ? station[config.bondKey] ?? 0 : 0,
        });
      });
    });

    // Station dropdown lists every accessible station (counts may be 0 for the range).
    setStationOptions(rows.map((r) => ({ name: r.station, circle: r.circle })));
    setSummaryRows(rows);
  }, [startDate, endDate, dateBasis, userId, config.psColumn, config.bondKey]);

  const loadDocuments = useCallback(
    async (pageNum: number) => {
      const res: any = await apiService.get(`/documents`, buildDocParams(pageNum), { noCache: true });
      setDocuments((res.data || []).map(toDetailRow));
      setTotal(res.pagination?.total ?? 0);
      setTotalPages(res.pagination?.totalPages ?? 0);
      setPage(pageNum);
    },
    [buildDocParams]
  );

  // Initial load.
  useEffect(() => {
    loadSummary();
    loadDocuments(1);
    // Reset when switching between type pages.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [typeKey]);

  const handleApply = () => {
    loadSummary();
    loadDocuments(1);
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setPoliceStation("all");
    setRiskLevel("all");
  };

  const handleView = (doc: any) => {
    setSelectedDocument(doc);
    setShowDocumentDetail(true);
  };

  // Summary rows honor the police-station filter on the client (the ps-wise
  // endpoint returns every accessible station regardless of that filter).
  const displayedSummary = useMemo(
    () => (policeStation === "all" ? summaryRows : summaryRows.filter((r) => r.station === policeStation)),
    [summaryRows, policeStation]
  );

  const summaryTotals = useMemo(() => {
    const totalDocs = displayedSummary.reduce((acc, r) => acc + r.count, 0);
    const totalBond = displayedSummary.reduce((acc, r) => acc + r.bond, 0);
    const stationsWithCount = displayedSummary.filter((r) => r.count > 0).length;
    return { totalDocs, totalBond, stationsWithCount };
  }, [displayedSummary]);

  // ---- Export (filtered detail list, all pages) ----
  const buildExportRows = async () => {
    const res: any = await apiService.get(`/documents`, buildDocParams(1, true), { noCache: true });
    return (res.data || []).map(toDetailRow);
  };

  const downloadBlob = (content: string, mime: string, ext: string) => {
    const blob = new Blob([content], { type: mime });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", `${config.psColumn}_report_${new Date().toISOString().split("T")[0]}.${ext}`);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToCSV = async () => {
    const rows = await buildExportRows();
    const headers = ["S. No.", "GD No", "Case Type", "Officer", "Police Station", "GD Date", "Upload Date", "Bond Amount (INR)", "Risk Level"];
    const body = rows.map((r: any, i: number) => [i + 1, r.gdNo ?? "", r.caseType ?? "", r.officer ?? "", r.station ?? "", r.gdDate ?? "", r.uploadDate ?? "", r.bondAmount ?? 0, r.riskLevel ?? ""]);
    const csv = [headers.join(","), ...body.map((row: any[]) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))].join("\n");
    downloadBlob(csv, "text/csv;charset=utf-8;", "csv");
  };

  const exportToExcel = async () => {
    const rows = await buildExportRows();
    let html = `
      <html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
      <head><meta charset="utf-8">
      <style>table{border-collapse:collapse;width:100%}th,td{border:1px solid #ddd;padding:8px;text-align:left}th{background:#f2f2f2;font-weight:bold}</style>
      </head><body>
      <h3>${config.label}</h3>
      <table><thead><tr>
        <th>S. No.</th><th>GD No</th><th>Case Type</th><th>Officer</th><th>Police Station</th><th>GD Date</th><th>Upload Date</th><th>Bond Amount (INR)</th><th>Risk Level</th>
      </tr></thead><tbody>`;
    rows.forEach((r: any, i: number) => {
      html += `<tr><td>${i + 1}</td><td>${r.gdNo ?? ""}</td><td>${r.caseType ?? ""}</td><td>${r.officer ?? ""}</td><td>${r.station ?? ""}</td><td>${r.gdDate ?? ""}</td><td>${r.uploadDate ?? ""}</td><td>${r.bondAmount ?? 0}</td><td>${r.riskLevel ?? ""}</td></tr>`;
    });
    html += `</tbody></table></body></html>`;
    downloadBlob(html, "application/vnd.ms-excel", "xls");
  };

  const startIndex = (page - 1) * ITEMS_PER_PAGE;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-heading font-bold text-foreground">{config.label}</h1>
              <p className="text-muted-foreground">Documents and station-wise summary for {config.label}</p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="flex items-center gap-2 h-8 cursor-pointer">
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={exportToCSV} className="flex items-center gap-2 cursor-pointer">
                  <FileText className="h-4 w-4" />
                  Export as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportToExcel} className="flex items-center gap-2 cursor-pointer">
                  <FileSpreadsheet className="h-4 w-4" />
                  Export as Excel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-6 space-y-6">
        {/* Filters */}
        <div className="rounded-lg border bg-card p-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="space-y-1">
              <Label className="text-xs">Start Date</Label>
              <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="h-8 w-40" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">End Date</Label>
              <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="h-8 w-40" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Police Station</Label>
              <Select value={policeStation} onValueChange={setPoliceStation}>
                <SelectTrigger className="h-8 w-52">
                  <SelectValue placeholder="All Stations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Stations</SelectItem>
                  {stationOptions.map((s) => (
                    <SelectItem key={s.name} value={s.name}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Risk Level</Label>
              <Select value={riskLevel} onValueChange={setRiskLevel}>
                <SelectTrigger className="h-8 w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {RISK_OPTIONS.map((o) => (
                    <SelectItem key={o.value} value={o.value}>
                      {o.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="secondary" className="cursor-pointer" onClick={handleApply}>
                Apply Filters
              </Button>
              <Button size="sm" variant="outline" className="cursor-pointer" onClick={handleReset}>
                Reset
              </Button>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">Risk Level filter applies to the detail list only.</p>
        </div>

        {/* Summary cards */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-lg border bg-card p-4">
            <div className="text-xs text-muted-foreground">Total Documents</div>
            <div className="text-2xl font-bold">{summaryTotals.totalDocs}</div>
          </div>
          {config.bondKey && (
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs text-muted-foreground">Total Bond Amount</div>
              <div className="text-2xl font-bold text-blue-700">{summaryTotals.totalBond > 0 ? `₹${summaryTotals.totalBond.toLocaleString("en-IN")}` : "-"}</div>
            </div>
          )}
          <div className="rounded-lg border bg-card p-4">
            <div className="text-xs text-muted-foreground">Stations with Documents</div>
            <div className="text-2xl font-bold">{summaryTotals.stationsWithCount}</div>
          </div>
        </div>

        {/* Station-wise breakdown */}
        <div className="rounded-lg border bg-card p-4">
          <div className="mb-3 text-sm font-medium">Station-wise Breakdown</div>
          <Table className="text-xs">
            <TableHeader>
              <TableRow className="bg-gray-50">
                <TableHead className="text-black font-semibold py-2 px-2">Circle</TableHead>
                <TableHead className="text-black font-semibold py-2 px-2">Police Station</TableHead>
                <TableHead className="text-black font-semibold py-2 px-2 text-center">Count</TableHead>
                {config.bondKey && <TableHead className="text-black font-semibold py-2 px-2 text-center text-blue-700">{config.bondLabel}</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayedSummary.map((r) => (
                <TableRow key={r.id} className="hover:bg-gray-50">
                  <TableCell className="py-1 px-2 text-gray-600">{r.circle}</TableCell>
                  <TableCell className="py-1 px-2 text-gray-600">{r.station}</TableCell>
                  <TableCell className="py-1 px-2 text-center">{r.count}</TableCell>
                  {config.bondKey && <TableCell className="py-1 px-2 text-center text-blue-700">{r.bond > 0 ? `₹${r.bond.toLocaleString("en-IN")}` : "-"}</TableCell>}
                </TableRow>
              ))}
              <TableRow className="bg-gray-50 font-semibold">
                <TableCell className="py-1 px-2 text-gray-800">Total</TableCell>
                <TableCell className="py-1 px-2" />
                <TableCell className="py-1 px-2 text-center">{summaryTotals.totalDocs}</TableCell>
                {config.bondKey && <TableCell className="py-1 px-2 text-center text-blue-700">{summaryTotals.totalBond > 0 ? `₹${summaryTotals.totalBond.toLocaleString("en-IN")}` : "-"}</TableCell>}
              </TableRow>
            </TableBody>
          </Table>
        </div>

        {/* Detail list */}
        <div className="rounded-lg border bg-card p-4">
          <div className="mb-3 flex items-center justify-between">
            <div className="text-sm font-medium">Document Details</div>
            <div className="text-xs text-muted-foreground">{total} documents found</div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>GD No</TableHead>
                <TableHead>Case Type</TableHead>
                <TableHead>Officer</TableHead>
                <TableHead>Police Station</TableHead>
                <TableHead>GD Date</TableHead>
                <TableHead>Upload Date</TableHead>
                <TableHead>Bond Amount</TableHead>
                <TableHead>Risk Level</TableHead>
                <TableHead>View</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {documents.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell className="font-medium">{doc.gdNo}</TableCell>
                  <TableCell>{doc.caseType}</TableCell>
                  <TableCell>{doc.officer}</TableCell>
                  <TableCell>{doc.station}</TableCell>
                  <TableCell>{doc.gdDate}</TableCell>
                  <TableCell>{doc.uploadDate}</TableCell>
                  <TableCell>{doc.bondAmount ? `₹${doc.bondAmount.toLocaleString("en-IN")}` : "-"}</TableCell>
                  <TableCell>
                    <Badge variant={doc.riskLevel === "High" ? "destructive" : doc.riskLevel === "Medium" ? "default" : "secondary"}>{doc.riskLevel}</Badge>
                  </TableCell>
                  <TableCell>
                    <Button className="cursor-pointer" size="sm" variant="ghost" onClick={() => handleView(doc.data)}>
                      <Eye className="h-4 w-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>

          {documents.length === 0 && <div className="text-center py-8 text-gray-500">No documents found.</div>}

          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="text-sm text-gray-600">
                Showing {startIndex + 1} to {Math.min(startIndex + ITEMS_PER_PAGE, total)} of {total} documents
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => loadDocuments(page - 1)} disabled={page === 1} className="border-gray-300">
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>
                <div className="flex items-center gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 1)
                    .map((p, index, array) => (
                      <div key={p} className="flex items-center">
                        {index > 0 && array[index - 1] !== p - 1 && <span className="px-2 text-gray-400">...</span>}
                        <Button variant={page === p ? "default" : "outline"} size="sm" onClick={() => loadDocuments(p)} className={page === p ? "bg-black text-white" : "border-gray-300"}>
                          {p}
                        </Button>
                      </div>
                    ))}
                </div>
                <Button variant="outline" size="sm" onClick={() => loadDocuments(page + 1)} disabled={page === totalPages} className="border-gray-300">
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      {selectedDocument && showDocumentDetail && <DocumentViewPopup open={showDocumentDetail} onOpenChange={() => setShowDocumentDetail(false)} document={selectedDocument} />}
    </div>
  );
}
